import { useEffect, useMemo, useState } from "react";
import { queries } from "../../../../shared/queries";
import { useQuery } from "../../../hooks/useQuery";
import { tunnelRepoCredsPath } from "../../../lib/apiPaths";
import { apiUrl } from "../../../lib/tenantOrigin";
import { Box } from "../../Box";
import { CodeBlock } from "../../CodeBlock";
import { Text } from "../../Text";
import { Card } from "../shared";

const TUNNEL_IMAGE = "oci.tines.com/3b-tunnel/tunnel-exit-node:latest";
const TUNNEL_REPO_HOST = "oci.tines.com";

// Bash-single-quote escape: close, escaped quote, reopen. Every interpolation
// in the rendered shell commands goes through this — webhook-supplied creds,
// the tunnel token, and `destinationCidrs` (a manager-supplied string).
function shellSingleQuote(value: string): string {
  return `'${value.replaceAll("'", "'\\''")}'`;
}

function dockerLoginCommand(username: string, password: string): string {
  return `echo ${shellSingleQuote(password)} | docker login ${TUNNEL_REPO_HOST} -u ${shellSingleQuote(username)} --password-stdin`;
}

function dockerRunCommand(
  token: string | null,
  destinationCidrs: string
): string {
  const lines = [
    "docker run \\",
    "  -d \\",
    "  --pull=always \\",
    "  --restart=always \\",
    "  --name=tunnel-exit-node \\",
    `  -e TUNNEL_TOKEN=${shellSingleQuote(token ?? "<your tunnel token>")} \\`,
  ];
  if (destinationCidrs) {
    lines.push(
      `  -e DESTINATION_CIDRS=${shellSingleQuote(destinationCidrs)} \\`
    );
  }
  lines.push(`  ${TUNNEL_IMAGE}`);
  return lines.join("\n");
}

function Step({
  index,
  title,
  description,
  command,
}: {
  index: number;
  title: string;
  description: React.ReactNode;
  command: string;
}) {
  return (
    <Card>
      <Box bottom={4}>
        <Text variant="text-regular-md">
          Step {index}: {title}
        </Text>
      </Box>
      <Text variant="text-regular" color="var(--text-secondary)">
        {description}
      </Text>
      <CodeBlock wrap content={command} />
    </Card>
  );
}

function useRepoCreds(): { username: string; password: string } | null {
  const externalApiUrl = apiUrl();
  const [creds, setCreds] = useState<{
    username: string;
    password: string;
  } | null>(null);
  useEffect(() => {
    let cancelled = false;
    void fetch(`${externalApiUrl}${tunnelRepoCredsPath()}`, {
      credentials: "include",
    }).then(async (res) => {
      if (cancelled || !res.ok || res.status === 204) return;
      setCreds(await res.json());
    });
    return () => {
      cancelled = true;
    };
  }, [externalApiUrl]);
  return creds;
}

export function NetworkProvisioning({
  networkId,
  clientKeyPem = null,
}: {
  networkId: string;
  clientKeyPem?: string | null;
}) {
  const [networks, networksResult] = useQuery(queries.networks());
  const network = useMemo(
    () => networks.find((n) => n.id === networkId),
    [networks, networkId]
  );
  const repoCreds = useRepoCreds();

  const networkConfig = network?.configuration as {
    destinationCidrs?: string;
    clientCertSha256?: string;
    clientCertPem?: string;
    gatewayEndpoint?: string;
    gatewayCaPem?: string;
  } | null;
  const destinationCidrs = networkConfig?.destinationCidrs ?? "";
  const provisioned = !!networkConfig?.clientCertSha256;
  const tunnelToken = useMemo(() => {
    if (
      !clientKeyPem ||
      !networkConfig?.gatewayEndpoint ||
      !networkConfig?.clientCertPem ||
      !networkConfig?.gatewayCaPem
    ) {
      return null;
    }
    return btoa(
      JSON.stringify({
        gateway_addr: networkConfig.gatewayEndpoint,
        client_cert_pem: networkConfig.clientCertPem,
        client_key_pem: clientKeyPem,
        gateway_ca_pem: networkConfig.gatewayCaPem,
      })
    );
  }, [clientKeyPem, networkConfig]);

  if (!network && networksResult.type !== "complete") {
    return (
      <Card>
        <Text variant="text-regular" color="var(--text-secondary)">
          Loading…
        </Text>
      </Card>
    );
  }

  if (!provisioned) {
    return (
      <Card>
        <Text variant="text-regular-md">Provisioning tunnel…</Text>
        <Text variant="text-regular" color="var(--text-secondary)">
          Minting the tunnel’s client certificate. This usually finishes in a
          second or two.
        </Text>
      </Card>
    );
  }

  let stepIndex = 0;

  return (
    <>
      {tunnelToken && (
        <Card>
          <Box bottom={4}>
            <Text variant="text-regular-md">Tunnel token</Text>
          </Box>
          <Box bottom={8}>
            <Text variant="text-regular" color="var(--text-danger)">
              Copy this now and store it somewhere safe. It’s shown only once
              and won’t be visible after you close this dialog.
            </Text>
          </Box>
          <CodeBlock content={tunnelToken} />
        </Card>
      )}

      {repoCreds && (
        <Step
          index={++stepIndex}
          title="Sign in to the registry"
          description={
            <>
              The tunnel exit-node image is hosted at{" "}
              <code style={{ fontFamily: "var(--font-mono)" }}>
                {TUNNEL_REPO_HOST}
              </code>
              . Authenticate Docker on the exit-node host before pulling.
            </>
          }
          command={dockerLoginCommand(repoCreds.username, repoCreds.password)}
        />
      )}

      <Step
        index={++stepIndex}
        title="Run the exit node"
        description={
          tunnelToken ? (
            "Run this command from a host inside the private network. It dials the gateway over mTLS, so no inbound firewall rules are required."
          ) : (
            <>
              Run this command from a host inside the private network, with the
              tunnel token you saved when this network was created. If you’ve
              lost it, you’ll need to create a new network to bring another exit
              node online.
            </>
          )
        }
        command={dockerRunCommand(tunnelToken, destinationCidrs)}
      />

      <Step
        index={++stepIndex}
        title="Check logs"
        description="Once the container is running, tail its logs to confirm the tunnel handshake succeeded."
        command="docker logs -f tunnel-exit-node"
      />
    </>
  );
}
